import { ExcelExportProperties } from '@syncfusion/ej2-grids';

export async function exportGridToExcel(
    gridInstance: any,
    formatDates: (date: any) => string,
    fileName: string = 'ExportedReport',
) {
    if (!gridInstance) {
        console.error('Grid instance not found');
        return;
    }
    const exportProps: ExcelExportProperties = {
        fileName: `${fileName}_${new Date().toISOString().slice(0, 10)}.xlsx`,
        exportType: 'AllPages',
    };
    try {
        await gridInstance.excelExport(exportProps);
    } catch (error) {
        console.error('Export failed:', error);
    }
}

export function getFilters(
    dashboardService: any,
    getDashboardParams: (isInit: boolean) => any,
    user: any,
    setFilters: (filters: any) => void,
    activityState: (state: boolean) => void,
) {
    activityState(true);

    dashboardService.getFitlersData(getDashboardParams(true)).subscribe(
        (res: any) => {
            if (res?.result) {
                const filters = res?.result;
                let productList = filters['products'] || [];
                let storeList = filters['stores'] || [];
                let wholesalerList = filters['wholesalers'] || [];
                let businessUnitList = filters['supplierBusinessUnits'] || [];
                let provinceList = filters['provinces'] || [];
                let supplierList = filters['suppliers'] || [];
                let categoryList = filters['categories'] || [];

                if (user?.isEnterprise() && (!businessUnitList || businessUnitList.length === 0)) {
                    const companyName = user.enterprise?.businessName || 'Default Enterprise Name';
                    const enterpriseBuId = `enterprise_${user.enterprise?.enterpriseId || 'default_id'}_bu`;
                    businessUnitList = [
                        {
                            id: enterpriseBuId,
                            name: companyName,
                            displayName: companyName,
                        },
                    ];
                }

                setFilters({
                    products: productList,
                    stores: storeList,
                    businessUnits: businessUnitList,
                    provinces: provinceList,
                    wholesalers: wholesalerList,
                    suppliers: supplierList,
                    categories: categoryList,
                });
            } else {
                console.error('Invalid filter response structure:', res);
            }
            activityState(false);
        },
        (error: any) => {
            console.error('Error fetching filters:', error);
            activityState(false);
        },
    );
}

export function applyGridSearch(
    gridInstance: any,
    filterOperatorMap: any,
    filters: { key: string; value: any; operators: string }[]
) {
    const newFilterColumns = [];
    for (const { key, value, operators } of filters) {
        const operator = filterOperatorMap[operators]
        // Removal must match the field actually pushed below (operators, singular backend
        // field name), not `key` (the plural form-control name) - otherwise stale entries for
        // this field are never cleared and pile up as the selection changes.
        const filterCol = operators;
        const normalizedValue = Array.isArray(value) && value.length === 1 ? value[0] : value;

        const hasArg = Array.isArray(normalizedValue)
            ? !!normalizedValue.length
            : normalizedValue != null && normalizedValue !== '';
        if (gridInstance.filterSettings.columns?.length) {
            gridInstance.filterSettings.columns = gridInstance.filterSettings.columns.filter(
                (fColumn: any) => fColumn.field !== filterCol,
            );
        }
        if (hasArg) {
            if (Array.isArray(normalizedValue)) {
                // The backend's generic query builder has no "IN" operator - only scalar
                // "equal". Mirror Syncfusion's own filterByColumn array handling: one equal
                // predicate per selected value, OR'd together on the same field.
                for (const singleValue of normalizedValue) {
                    newFilterColumns.push({
                        field: operators,
                        operator,
                        value: singleValue,
                        predicate: 'or'
                    });
                }
            } else {
                newFilterColumns.push({
                    field: operators,
                    operator,
                    value: normalizedValue,
                    predicate: 'and'
                });
            }
        } else {
            gridInstance.removeFilteredColsByField?.(operators);
        }
    }

    gridInstance.filterSettings.columns = [
        ...(gridInstance.filterSettings.columns || []),
        ...newFilterColumns
    ];

    gridInstance.dataBind();
}

export function getBaseParams(user: any) {
    const params: { [key: string]: string } = { type: 'buy' };

    if (user?.isSupplierEnterprise()) {
        params.supplierIds = user.enterprise.supplier.id;
    }
    return params;
}

export function getDashboardParams(
    form?: any,
    user?: any,
    filterOperatorMap?: any,
    applyGridSearchFn?: any,
    isInit?: boolean,
    changedFields?: string
) {
    let filterOperatorMaps: { [key: string]: string } = {
        fromDate: 'fromDate',
        toDate: 'toDate',
        supplierBusinessUnitIds: 'supplierBusinessUnitId',
        productIds: 'productId',
        provinceIds: 'provinceId',
        storeIds: 'storeId',
        wholesalerIds: 'wholesalerId',
        supplierIds: 'supplierId',
        categoryIds: 'categoryId',
    };
    const { toDateFilter, fromDateFilter, ...formValue } = form.value;
    const queryParams: { [key: string]: any } = {
        ...getBaseParams(user),
    };
    const filtersToApply: { key: string; value: any; operators: string }[] = [];

    Object.entries(formValue).forEach(([key, value]) => {
        const hasValue = value !== null && value !== undefined && (!Array.isArray(value) || value.length > 0);
        if (hasValue) {
            queryParams[key] = Array.isArray(value) ? value.join(',') : value;
        }
        if (changedFields && changedFields !== 'NoGridSearch') {
            if (isInit === false && (!changedFields || changedFields.includes(key))) {
                const operators = filterOperatorMaps[key];
                if (operators) {
                    filtersToApply.push({ key, value, operators });
                }
            }
        }
    });
    if (filtersToApply.length && typeof applyGridSearchFn === 'function') {
        applyGridSearchFn(filtersToApply);
    }
    return queryParams;
}

export function getData(
    dashboardService: any,
    getDashboardParams: (isInit: boolean, changedFields: string) => any,
    user: any,
    form: any,
    searchItemsObj: any,
    setSearchItems: (items: any) => void,
    setFilters: (filters: any) => void,
    activityState: (state: boolean) => void,
    changedFields: string
) {
    activityState(true);

    const params = getDashboardParams(false, changedFields);

    dashboardService.getFitlersData(params).subscribe(
        (res: any) => {

            if (res?.result) {
                const searchItems: any = {};
                for (const key in searchItemsObj) {
                    if (form.get(key)?.value?.length > 0) {
                        searchItems[key] = searchItemsObj[key];
                    }
                }
                setSearchItems(searchItems);
            } else {
                console.error('Invalid data response structure:', res);
            }

            if (res?.result) {
                const filters = res.result;

                let productList = filters['products'] || [];
                let storeList = filters['stores'] || [];
                let wholesalerList = filters['wholesalers'] || [];
                let businessUnitList = filters['supplierBusinessUnits'] || [];
                let provinceList = filters['provinces'] || [];
                let supplierList = filters['suppliers'] || [];
                let categoryList = filters['categories'] || [];

                if (user?.isEnterprise() && (!businessUnitList || businessUnitList.length === 0)) {
                    const companyName = user.enterprise?.businessName || 'Default Enterprise Name';
                    const enterpriseBuId = `enterprise_${user.enterprise?.enterpriseId || 'default_id'}_bu`;
                    businessUnitList = [
                        {
                            id: enterpriseBuId,
                            name: companyName,
                            displayName: companyName,
                        },
                    ];
                }

                setFilters({
                    products: productList,
                    stores: storeList,
                    businessUnits: businessUnitList,
                    provinces: provinceList,
                    wholesalers: wholesalerList,
                    suppliers: supplierList,
                    categories: categoryList,
                });
            }

            activityState(false);
        },
        (error: any) => {
            console.error('Error fetching sales data + filters:', error);
            activityState(false);
        },
    );
}
